import React, { useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { Camera, Sparkles, ArrowRight, CheckCircle, Star, Crown, Zap, Shield, Clock } from 'lucide-react';
import CorporateHeader from './CorporateHeader';
import Footer from './Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AIPhotoshootsPage = () => {
  const navigate = useNavigate();
  const pricingRef = useRef(null);
  const lookbookRef = useRef(null);

  const scrollToPricing = () => pricingRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handleSelectPlan = (packageId) => {
    const fetchCheckout = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ai-service/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin })
        });
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else alert('Unable to start checkout. Please try again.');
      } catch {
        alert('Unable to process. Please try again.');
      }
    };
    fetchCheckout();
  };

  const scenes = [
    { name: 'CEO Power', desc: 'Boardroom authority and executive presence', image: 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/0srkpw5p_2.%20CEO%20BOARDROOM%20Egypt%20-%20in%20a%203%20piece%20suit.png' },
    { name: 'Corporate Headshots', desc: 'Polished, professional business portraits', image: 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/mklr5ala_Egypt%20-%20Signing%20contract.png' },
    { name: 'Classroom / Teacher', desc: 'Warm, approachable educational settings', image: 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/5pqsoz13_03.%20Professor%20-%20Tyree%20-%20example%203.png' },
    { name: 'City / Urban Professional', desc: 'Sleek cityscapes and urban backdrops', image: 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/1dtzgm66_3.%20City%20Skyline%20Egypt%20-%20Holding%20computer.png' },
    { name: 'Luxury Lifestyle', desc: 'Premium settings, editorial fashion', image: 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/tlmf42gy_6.%20LUXURY%20Lifestyle%20Private%20Jet%20-%20Egypt%20-%20on%20PJ%20Looking%20out%20the%20window.png' },
    { name: 'Creative / Studio', desc: 'Artistic lighting and bold compositions', image: 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/h089q4ob_Egypt%20-%20holding%20camera%20in%20beidge%20jacket.png' },
    { name: 'Magazine Headshots', desc: 'Cover-ready editorial quality', image: 'https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/ofsb8laz_4.%20Magazine%20cover%20-%20Signing%20Deal.png' },
  ];

  const steps = [
    { num: '01', title: 'Choose Your Package', desc: 'Select the experience that fits your brand goals' },
    { num: '02', title: 'Complete Payment', desc: 'Secure checkout via Stripe' },
    { num: '03', title: 'Upload Your Photo(s)', desc: 'Submit 1-2 clear photos of yourself' },
    { num: '04', title: 'Receive Preview Proofs', desc: 'We send numbered preview images for your review' },
    { num: '05', title: 'Select Your Finals', desc: 'Choose your favorite images for high-res delivery' },
  ];

  const packages = [
    {
      id: 'starter_photoshoot',
      name: 'The Starter Look',
      price: 49,
      popular: false,
      features: ['1 style / look', '6 preview images', 'Choose 2 final images', 'Digital delivery', '48-hour turnaround'],
    },
    {
      id: 'brand_builder_photoshoot',
      name: 'The Brand Builder',
      price: 99,
      popular: true,
      features: ['2 styles / looks', '10-12 preview images', 'Choose 5 final images', 'Press kit ready', 'Priority 24-hour turnaround', '1 revision round'],
    },
    {
      id: 'ceo_experience_photoshoot',
      name: 'The CEO Experience',
      price: 149,
      popular: false,
      features: ['3-4 styles / looks', '15 preview images', 'Choose 8-10 final images', 'Full brand kit delivery', 'VIP support', '2 revision rounds', 'Social media pack'],
    },
  ];

  const addons = [
    { name: 'Extra Image', price: '+$10' },
    { name: 'Rush Delivery', price: '+$25' },
    { name: 'Custom Location', price: '+$15' },
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />

      {/* HERO */}
      <section className="pt-28 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-yellow-900/15 via-transparent to-transparent" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-600/50 to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 bg-amber-900/20 border border-amber-700/30 text-amber-400 text-xs font-semibold tracking-[0.2em] uppercase px-5 py-2 rounded-full mb-8">
            <Camera className="w-4 h-4" />
            eBlackBook AI Luxury Photoshoots
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-4 tracking-tight leading-[1.1]" data-testid="hero-title">
            Your Brand. <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">Elevated.</span>
          </h1>
          <p className="text-xl sm:text-2xl text-amber-400/90 font-light italic mb-6">
            "We Don't Take Pictures... We Build Presence."
          </p>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            One clear photo. Unlimited possibilities. Our AI-powered service transforms a single image into a luxury professional photoshoot — any scene, any setting, no camera needed.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={scrollToPricing} className="bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold px-10 py-4 text-lg rounded-full shadow-lg shadow-amber-900/30" data-testid="cta-start">
              Book Your AI Photoshoot Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => lookbookRef.current?.scrollIntoView({ behavior: 'smooth' })} variant="outline" className="border-amber-700/50 text-amber-400 hover:bg-amber-900/20 px-8 py-4 text-lg rounded-full">
              View the Lookbook
            </Button>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-950 to-black border-t border-gray-900" ref={lookbookRef}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="text-center mb-14">
            <p className="text-amber-400 text-sm tracking-[0.2em] uppercase font-semibold mb-3">The Transformation</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">From One Photo to Full Brand Presence</h2>
            <p className="text-gray-400 max-w-xl mx-auto">See how a single image becomes a library of premium, brand-ready visuals.</p>
          </div>
          {/* Before & After */}
          <div className="grid md:grid-cols-2 gap-8 mb-12 items-center">
            <div className="text-center">
              <div className="relative rounded-2xl overflow-hidden border-2 border-gray-800 shadow-xl">
                <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/1uaek2rp_1.%20Original%20-%20Egypt%20Original%20pic%20.png" alt="Original Photo" className="w-full object-cover" />
              </div>
              <p className="text-gray-500 text-sm uppercase tracking-wider font-semibold mt-4">Original Photo</p>
            </div>
            <div className="text-center">
              <div className="grid grid-cols-2 gap-3">
                <div className="relative rounded-xl overflow-hidden border border-amber-700/40 shadow-lg group">
                  <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/0srkpw5p_2.%20CEO%20BOARDROOM%20Egypt%20-%20in%20a%203%20piece%20suit.png" alt="CEO Boardroom" className="w-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <span className="text-amber-400 text-xs font-semibold">CEO Boardroom</span>
                  </div>
                </div>
                <div className="relative rounded-xl overflow-hidden border border-amber-700/40 shadow-lg group">
                  <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/1dtzgm66_3.%20City%20Skyline%20Egypt%20-%20Holding%20computer.png" alt="City Skyline" className="w-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <span className="text-amber-400 text-xs font-semibold">City Skyline</span>
                  </div>
                </div>
                <div className="relative rounded-xl overflow-hidden border border-amber-700/40 shadow-lg group">
                  <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/ofsb8laz_4.%20Magazine%20cover%20-%20Signing%20Deal.png" alt="Magazine Cover" className="w-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <span className="text-amber-400 text-xs font-semibold">Magazine Cover</span>
                  </div>
                </div>
                <div className="relative rounded-xl overflow-hidden border border-amber-700/40 shadow-lg group">
                  <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/5llmgclk_5.%20Creative%20Studio%20Egypt%20-%20holding%20Camera.png" alt="Creative Studio" className="w-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <span className="text-amber-400 text-xs font-semibold">Creative Studio</span>
                  </div>
                </div>
              </div>
              <p className="text-amber-400 text-sm uppercase tracking-wider font-semibold mt-4">AI Luxury Photoshoot Results</p>
            </div>
          </div>
          {/* Lookbook CTA */}
          <div className="text-center mb-8">
            <a href="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/girmjpvs_eBlackBook_AI_Lookbook_COMPLETE_FINAL.pdf" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-amber-700/50 text-amber-400 hover:bg-amber-900/20 px-8 py-3 rounded-full" data-testid="view-lookbook-btn">
                <ArrowRight className="w-4 h-4 mr-2" />
                View the eBlackBook AI Luxury Photoshoots Lookbook
              </Button>
            </a>
          </div>

          {/* Second Transformation — Tyree */}
          <div className="mt-16 mb-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-center">
                <div className="relative rounded-2xl overflow-hidden border-2 border-gray-800 shadow-xl">
                  <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/z4yg24h7_01.%20original%20-%20TYREE%20-%20original%20pic.png" alt="Tyree - Original Photo" className="w-full object-cover" />
                </div>
                <p className="text-gray-500 text-sm uppercase tracking-wider font-semibold mt-4">Original Photo</p>
              </div>
              <div className="text-center">
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative rounded-xl overflow-hidden border border-amber-700/40 shadow-lg group">
                    <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/809q3sss_05.%20BOARDROOM%20-%20Tyree%20-%20example%201.png" alt="Boardroom" className="w-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <span className="text-amber-400 text-xs font-semibold">Boardroom</span>
                    </div>
                  </div>
                  <div className="relative rounded-xl overflow-hidden border border-amber-700/40 shadow-lg group">
                    <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/3db4qodq_02.%20City%20Boss%20-%20Tyree%20-%20example%202.png" alt="City Boss" className="w-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <span className="text-amber-400 text-xs font-semibold">City Boss</span>
                    </div>
                  </div>
                  <div className="relative rounded-xl overflow-hidden border border-amber-700/40 shadow-lg group">
                    <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/5pqsoz13_03.%20Professor%20-%20Tyree%20-%20example%203.png" alt="Professor" className="w-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <span className="text-amber-400 text-xs font-semibold">Professor</span>
                    </div>
                  </div>
                  <div className="relative rounded-xl overflow-hidden border border-amber-700/40 shadow-lg group">
                    <img src="https://customer-assets.emergentagent.com/job_714c4da9-c687-4be7-8ba1-ec382daddaca/artifacts/0f6yzgbj_04.%20TAKING%20CHARGE%20-%20Tyree%20example.png" alt="Taking Charge" className="w-full object-cover aspect-square group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <span className="text-amber-400 text-xs font-semibold">Taking Charge</span>
                    </div>
                  </div>
                </div>
                <p className="text-amber-400 text-sm uppercase tracking-wider font-semibold mt-4">AI Luxury Photoshoot Results</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {['LinkedIn', 'Press Kits', 'Business Branding', 'Book Covers', 'Speaking Features'].map((use) => (
              <span key={use} className="text-gray-500 text-sm border border-gray-800 px-4 py-2 rounded-full">{use}</span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-black border-t border-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="text-center mb-14">
            <p className="text-amber-400 text-sm tracking-[0.2em] uppercase font-semibold mb-3">The Process</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">How It Works</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="text-center group">
                <div className="text-3xl font-black text-amber-600/30 mb-3 group-hover:text-amber-400 transition-colors">{step.num}</div>
                <h3 className="text-white font-bold mb-2 text-sm">{step.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCENE OPTIONS */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-950 border-t border-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="text-center mb-14">
            <p className="text-amber-400 text-sm tracking-[0.2em] uppercase font-semibold mb-3">Looks & Scenes</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Choose Your Setting</h2>
            <p className="text-gray-400 max-w-xl mx-auto">From boardrooms to cityscapes, select the scenes that match your brand vision.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {scenes.map((scene) => (
              <div key={scene.name} className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden hover:border-amber-600/40 transition-all group cursor-default">
                <div className="aspect-square overflow-hidden">
                  <img src={scene.image} alt={scene.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <h3 className="text-white font-bold text-sm mb-1">{scene.name}</h3>
                  <p className="text-gray-500 text-xs">{scene.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section ref={pricingRef} className="py-24 bg-black border-t border-gray-900" id="pricing">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          {/* Limited Spots Banner */}
          <div className="text-center mb-10">
            <div className="inline-block bg-red-900/30 border border-red-700/50 text-red-400 font-bold text-sm tracking-wide uppercase px-6 py-3 rounded-full">
              We only accept 10 clients per week to maintain quality.
            </div>
          </div>

          <div className="text-center mb-14">
            <p className="text-amber-400 text-sm tracking-[0.2em] uppercase font-semibold mb-3">Investment</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Choose Your Experience</h2>
            <p className="text-gray-400">This is not about taking pictures. This is about positioning.</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            {packages.map((pkg) => (
              <div key={pkg.id} className={`relative bg-gray-900/50 border rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 ${pkg.popular ? 'border-amber-500 shadow-lg shadow-amber-900/20' : 'border-gray-800'}`}>
                {pkg.popular && (
                  <div className="bg-gradient-to-r from-amber-600 to-yellow-600 text-black text-xs font-bold tracking-[0.15em] uppercase text-center py-2">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-xl font-bold text-white mb-2">{pkg.name}</h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-black text-amber-400">${pkg.price}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleSelectPlan(pkg.id)}
                    className={`w-full font-bold py-3 rounded-full ${pkg.popular ? 'bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                    data-testid={`select-${pkg.id}`}
                  >
                    Get Your Photoshoot Started
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {/* Add-ons */}
          <div className="max-w-lg mx-auto bg-gray-900/50 border border-gray-800 rounded-xl p-6">
            <h4 className="text-white font-bold text-center mb-4 text-sm tracking-wider uppercase">Add-Ons</h4>
            <div className="flex flex-wrap justify-center gap-4">
              {addons.map((addon) => (
                <span key={addon.name} className="text-gray-400 text-sm border border-gray-700 px-4 py-2 rounded-full">
                  {addon.name} <span className="text-amber-400 font-semibold">{addon.price}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-950 border-t border-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-amber-400 text-sm tracking-[0.2em] uppercase font-semibold mb-3">Social Proof</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">What Our Clients Are Saying</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "I've never had professional photos like this before. The quality is unreal — people think I hired a full production team.", name: "Tameka R.", title: "Entrepreneur & Author" },
              { quote: "This changed my brand instantly. I went from basic selfies to looking like I belong on a magazine cover. Worth every penny.", name: "Marcus D.", title: "Business Consultant" },
              { quote: "I used my photos for LinkedIn, my book cover, and a speaking engagement flyer. One photoshoot gave me everything I needed.", name: "Jasmine K.", title: "Life Coach & Speaker" },
            ].map((testimonial, i) => (
              <div key={i} className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:border-amber-600/30 transition-all">
                <div className="flex text-amber-400 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-gray-300 italic leading-relaxed mb-6">"{testimonial.quote}"</p>
                <div>
                  <p className="text-white font-bold text-sm">{testimonial.name}</p>
                  <p className="text-gray-500 text-xs">{testimonial.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-950 border-t border-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            <div>
              <Shield className="w-8 h-8 text-amber-400 mx-auto mb-3" />
              <h4 className="text-white font-bold text-sm mb-1">Trusted Quality</h4>
              <p className="text-gray-500 text-xs">Used for LinkedIn, press kits, publications, and professional branding</p>
            </div>
            <div>
              <Clock className="w-8 h-8 text-amber-400 mx-auto mb-3" />
              <h4 className="text-white font-bold text-sm mb-1">Limited Spots</h4>
              <p className="text-gray-500 text-xs">We accept a limited number of clients per week to maintain quality</p>
            </div>
            <div>
              <Crown className="w-8 h-8 text-amber-400 mx-auto mb-3" />
              <h4 className="text-white font-bold text-sm mb-1">Powered By</h4>
              <p className="text-gray-500 text-xs">eBlackBookListings — Digital publishing and brand infrastructure</p>
            </div>
          </div>
        </div>
      </section>

      {/* POLICY */}
      <section className="py-12 bg-black border-t border-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <div className="bg-gray-900/30 border border-gray-800/50 rounded-xl p-6">
            <h4 className="text-white font-bold text-sm tracking-wider uppercase text-center mb-4">Service Terms</h4>
            <ul className="space-y-2 text-gray-500 text-xs leading-relaxed">
              <li className="flex items-start gap-2"><span className="text-amber-600 mt-0.5">&#8226;</span>Payment is required before any work begins</li>
              <li className="flex items-start gap-2"><span className="text-amber-600 mt-0.5">&#8226;</span>Turnaround starts after intake form and photos are submitted</li>
              <li className="flex items-start gap-2"><span className="text-amber-600 mt-0.5">&#8226;</span>Final image quality depends on uploaded photo quality</li>
              <li className="flex items-start gap-2"><span className="text-amber-600 mt-0.5">&#8226;</span>AI-generated service with professional human curation</li>
              <li className="flex items-start gap-2"><span className="text-amber-600 mt-0.5">&#8226;</span>Number of final images depends on selected package</li>
            </ul>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-black border-t border-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-amber-900/15 via-transparent to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center max-w-3xl">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Elevate Your Image?</h2>
          <p className="text-gray-400 mb-4 leading-relaxed">
            Your image is your first deal. Invest in the presence that opens doors, secures opportunities, and positions you as the authority you are.
          </p>
          <p className="text-amber-400 font-semibold italic mb-8">"Your Brand. Any Location. No Camera Needed."</p>
          <Button onClick={scrollToPricing} className="bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold px-12 py-4 text-lg rounded-full shadow-lg shadow-amber-900/30" data-testid="cta-book">
            Transform My Image Now
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIPhotoshootsPage;
